import React, { Component } from 'react';
import { Modal } from 'react-bootstrap';
import { connect } from 'react-redux';
import { connectModal } from 'redux-modal';
import { reduxForm, Field } from 'redux-form';
import { toastr } from 'react-redux-toastr';
import moment from 'moment';
import Button from 'react-bootstrap-button-loader';
import get from 'lodash/get';
import { AdminApi } from '../../../../api';
import { setLoader, removeLoader } from '../../../../actions';
import { convertDollarsToMills, convertMillsToDollars } from '../../../../utils/helpers/currency';
import { objectDiff } from '../../../../utils/helpers/object_diff';

import {
  InputField,
  CheckboxField,
  DateField,
  SelectField,
  required,
  parseDecimal,
  number,
  minValue,
  dateGtnToday,
  dateGtnDate,
  isValidSlug,
} from '../../../form';

class StandardCampaignForm extends Component {
  onFormSubmit(values) {
    const { initialValues, formAction, resource, dispatch, campaigns, handleHide } = this.props;
    const campaignData = this.sanitizeCampaignData(initialValues, values);

    const args = formAction === 'Create' ? [campaignData] : [resource.id, campaignData];

    dispatch(setLoader('adminCampaignForm'));
    dispatch(AdminApi.actions[`selfServeCampaign${formAction}`].post(...args, (_error, success) => {
      let collection = [...campaigns.data];

      if (success) {
        if (formAction === 'Create') {
          collection.unshift(success.data);
        } else {
          collection = collection.map((obj) => {
            return obj.id === success.data.id ? success.data : obj;
          });
        }

        dispatch({
          type: AdminApi.events.campaign.actionSuccess,
          syncing: false,
          data: { ...campaigns, data: collection, meta: { ...campaigns.meta } }
        });

        toastr.success(`Successfully ${formAction === 'Create' ? 'created' : 'updated'} campaign!`);
        handleHide();
      } else {
        dispatch(removeLoader('adminCampaignForm'));
      }
    }));
  }

  sanitizeCampaignData(initialValues, values) {
    const campaignData = objectDiff(initialValues, values);

    if (campaignData.bid) { campaignData.bid = convertDollarsToMills(campaignData.bid); }
    if (campaignData.budget) { campaignData.budget = convertDollarsToMills(campaignData.budget); }

    return campaignData;
  }

  handleShow() {
    const { handleHide, resource, onShow } = this.props;
    handleHide();
    onShow(resource.uuid, resource);
  }

  fetchStatus() {
    const { initialValues } = this.props;

    if (initialValues && initialValues.status === 'removed') {
      return [{ label: 'Removed', value: 'removed' }];
    }

    return [{ label: 'Active', value: 'active' }, { label: 'Paused', value: 'paused' }];
  }

  endDateValidation = (endDate, allValues) => {
    if (allValues.details && !allValues.details.recurring && endDate) {
      const validGtnDate = dateGtnDate(endDate, allValues.details.start_date);
      const validTdyDate = dateGtnToday(endDate);
      if (validTdyDate) return validTdyDate;
      if (validGtnDate) return validGtnDate;
    }

    return undefined;
  }

  render() {
    const {
      show,
      handleHide,
      handleSubmit,
      heading,
      isLoading,
      formAction,
      accountOptions,
      isRecurring,
      initialValues,
    } = this.props;
    const startDatePassed = initialValues && moment().isAfter(initialValues.start_date) ? true : false;
    const isRemoved = initialValues && (initialValues.status === 'removed' ? true : false);


    return (
      <Modal show={show} onHide={handleHide}>
        <Modal.Header>
          <Modal.Title>{heading}</Modal.Title>
        </Modal.Header>

        <form
          onSubmit={handleSubmit(this.onFormSubmit.bind(this))}
          className='m-t'
        >
          <Modal.Body>
            <Field
              type='text'
              name='account_id'
              label='Account'
              validate={[required]}
              component={SelectField}
              disabled={formAction === 'Update'}
              options={accountOptions}
              normalize={field => (field ? field.value : field)}
            />
            <Field
              type='text'
              name='name'
              label='Name'
              validate={[required, isValidSlug]}
              component={InputField}
            />
            <Field
              type='text'
              name='budget'
              label='Budget ($)'
              validate={[required, number, minValue(1)]}
              component={InputField}
            />
            <Field
              type='number'
              name='bid'
              label='Bid ($)'
              validate={[required, number, minValue(0.01)]}
              component={InputField}
              normalize={(value) => {
                return parseDecimal(value);
              }}
            />
            <Field
              name='recurring'
              label='Recurring'
              type='checkbox'
              singleLine
              component={CheckboxField}
            />
            <Field
              type='text'
              name='status'
              label='Status'
              validate={[required]}
              component={SelectField}
              options={this.fetchStatus()}
              disabled={isRemoved}
              normalize={field => (field ? field.value : field)}
            />
            <Field
              type='text'
              name='start_date'
              label='Start Date'
              disabled={startDatePassed}
              validate={startDatePassed ? [] : [required, dateGtnToday]}
              component={DateField}
            />
            <Field
              type='text'
              name='end_date'
              label='End Date'
              validate={isRecurring ? [] : [required, this.endDateValidation]}
              disabled={isRecurring}
              component={DateField}
            />
            <Field
              name='is_open'
              label='Open Budget'
              type='checkbox'
              singleLine
              component={CheckboxField}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={handleHide}>Close</Button>
            <Button
              type='submit'
              loading={isLoading}
              bsStyle='info'
            >
              {formAction}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    );
  }
}

const ResourceForm = reduxForm({
  form: 'AdminStandardCampaignForm'
});

const ResourceModal = connectModal({
  name: 'AdminStandardCampaignModal'
});

const mapStateToProps = (state) => {
  const { loader, modal: { AdminStandardCampaignModal }, admin: { account }, form: { AdminStandardCampaignForm }, formAction } = state;
  const isLoading = loader.includes('adminStandardTemplateForm');
  const resource = get(AdminStandardCampaignModal, 'props.resource', {});
  const resourceName = get(AdminStandardCampaignModal, 'props.resourceName', null);
  const campaigns = get(state, 'admin.campaign.data', {});
  const accountOptions = (account.data.data || []).filter(a => a.self_serve === true)
    .map(a => ({ label: `${a.advertiser} - ${a.name}`, value: a.id }));
  const isRecurring = get(AdminStandardCampaignForm, 'values.recurring', false);

  let initialValues = {};
  
  if (resourceName) {
    initialValues = { ...resource };

    if (initialValues.bid) { initialValues.bid = convertMillsToDollars(initialValues.bid); }
    if (initialValues.budget) { initialValues.budget = convertMillsToDollars(initialValues.budget); }
  }

  return { isLoading, initialValues, campaigns, accountOptions, formAction, isRecurring };
};

export default connect(mapStateToProps)(ResourceModal(ResourceForm(StandardCampaignForm)));
